/**
 * Weather utility functions for forecast display
 */

import { getTomorrowIoWeatherCode } from './tomorrowIoWeatherCodes';
import { WEATHER_ICON_FILE_MAP } from './weatherIconFileMap';

/**
 * Calculate heat index from air temperature and relative humidity.
 * Uses NOAA Rothfusz regression formula (SR 90-23).
 *
 * @param T - Air temperature in Celsius
 * @param R - Relative humidity in percent (0-100)
 * @returns Heat index in Celsius, or null if inputs are invalid
 */
export function calculateHeatIndex(T: number, R: number): number | null {
  if (T == null || R == null || isNaN(T) || isNaN(R)) return null;
  if (R < 0 || R > 100) return null;

  // Convert Celsius to Fahrenheit for the formula
  const tF = (T * 9) / 5 + 32;

  // Simple formula for lower temps (Steadman-consistent)
  const hiSimple = 0.5 * (tF + 61 + (tF - 68) * 1.2 + R * 0.094);
  const hiAvg = (hiSimple + tF) / 2;

  let hi: number;

  if (hiAvg >= 80) {
    // Full Rothfusz regression
    const T2 = tF * tF;
    const R2 = R * R;
    hi =
      -42.379 +
      2.04901523 * tF +
      10.14333127 * R -
      0.22475541 * tF * R -
      0.00683783 * T2 -
      0.05481717 * R2 +
      0.00122874 * T2 * R +
      0.00085282 * tF * R2 -
      0.00000199 * T2 * R2;

    // Low humidity adjustment (RH < 13% and 80°F ≤ T ≤ 112°F)
    if (R < 13 && tF >= 80 && tF <= 112) {
      const adj = ((13 - R) / 4) * Math.sqrt(Math.abs(17 - Math.abs(tF - 95)) / 17);
      hi -= adj;
    }
    // High humidity adjustment (RH > 85% and 80°F ≤ T ≤ 87°F)
    else if (R > 85 && tF >= 80 && tF <= 87) {
      const adj = ((R - 85) / 10) * ((87 - tF) / 5);
      hi += adj;
    }
  } else {
    hi = hiAvg;
  }

  // Convert back to Celsius
  return ((hi - 32) * 5) / 9;
}

/**
 * Map weather codes to descriptive labels and icons
 */
export interface WeatherCode {
  code: string;
  label: string;
  icon: string; // Icon path
  color?: string; // Optional color for styling
}

/**
 * Get weather code information
 * All weather codes are now in Tomorrow.io format (4-5 digits)
 */
export function getWeatherCode(code: string | null | undefined): WeatherCode {
  if (!code) {
    return { code: '', label: 'Unknown', icon: '/weather-icons/0_unknown_large.png' };
  }
  
  const tomorrowIoInfo = getTomorrowIoWeatherCode(code);
  
  return {
    code: tomorrowIoInfo.code,
    label: tomorrowIoInfo.label,
    icon: tomorrowIoInfo.icon
  };
}

/**
 * Determine if a given hour (0-23) is day or night
 * Day: 6 AM to 6 PM (6-17)
 * Night: 6 PM to 6 AM (18-23, 0-5)
 */
function isDayTime(hour: number): boolean {
  return hour >= 6 && hour < 18;
}

/**
 * Convert a 4-digit weather code to a 5-digit day/night code based on the hour
 * @param code 4-digit weather code (e.g., "1000")
 * @param hour Hour of day (0-23) in the local timezone
 * @returns 5-digit code with day (0) or night (1) suffix, or original code if already 5 digits
 */
function convertToDayNightCode(code: string, hour: number): string {
  // If already 5 digits, return as-is
  if (code.length === 5) {
    return code;
  }
  
  // If not 4 digits, return as-is (shouldn't happen but be safe)
  if (code.length !== 4) {
    return code;
  }
  
  // Determine day (0) or night (1) suffix
  const suffix = isDayTime(hour) ? '0' : '1';
  
  return code + suffix;
}

/**
 * Get weather code information for hourly forecasts
 * Converts 4-digit codes to 5-digit day/night codes based on the forecast datetime
 * Checks if the day/night icon exists, falls back to 4-digit icon if not
 * @param code Weather code (may be 4 or 5 digits)
 * @param datetime Forecast datetime (used to determine day/night)
 * @param timezone Timezone for the datetime (defaults to UTC)
 */
export function getWeatherCodeForHourly(
  code: string | null | undefined,
  datetime: Date,
  timezone?: string
): WeatherCode {
  if (!code) {
    return { code: '', label: 'Unknown', icon: '/weather-icons/0_unknown_large.png' };
  }
  
  // If already 5 digits, use as-is
  if (code.length === 5) {
    return getWeatherCode(code);
  }
  
  // If 4 digits, convert to day/night based on local hour
  if (code.length === 4) {
    // Get hour in the specified timezone
    const hour = parseInt(
      datetime.toLocaleString('en-US', {
        timeZone: timezone || 'UTC',
        hour: '2-digit',
        hour12: false,
      }),
      10
    );
    
    // Convert to 5-digit day/night code
    const dayNightCode = convertToDayNightCode(code, hour);
    
    // Check if the day/night icon exists
    const dayNightIconExists = dayNightCode in WEATHER_ICON_FILE_MAP;
    
    if (dayNightIconExists) {
      // Use the day/night code
      const tomorrowIoInfo = getTomorrowIoWeatherCode(dayNightCode);
      return {
        code: tomorrowIoInfo.code,
        label: tomorrowIoInfo.label,
        icon: tomorrowIoInfo.icon
      };
    } else {
      // Fallback to 4-digit code
      const tomorrowIoInfo = getTomorrowIoWeatherCode(code);
      return {
        code: tomorrowIoInfo.code,
        label: tomorrowIoInfo.label,
        icon: tomorrowIoInfo.icon
      };
    }
  }
  
  // For other lengths, use as-is
  return getWeatherCode(code);
}

/**
 * Get weather icon from condition string (fallback for weather descriptions)
 * Used when no weather_code is available but weather_description text exists
 */
export function getWeatherIconFromCondition(condition: string | null | undefined): string {
  if (!condition) return '☀️'; // Default to sunny
  
  const lower = condition.toLowerCase();
  
  // Check for specific conditions
  if (lower.includes('thunder') || lower.includes('storm')) return '⛈️';
  if (lower.includes('heavy rain') || lower.includes('violent')) return '🌧️';
  if (lower.includes('rain') || lower.includes('shower')) return '🌧️';
  if (lower.includes('drizzle')) return '🌦️';
  if (lower.includes('snow') || lower.includes('flurr')) return '🌨️';
  if (lower.includes('fog') || lower.includes('mist')) return '🌫️';
  if (lower.includes('cloud') || lower.includes('overcast')) {
    if (lower.includes('partly')) return '⛅';
    return '☁️';
  }
  if (lower.includes('clear') || lower.includes('sunny') || lower.includes('fair')) return '☀️';
  
  return '☀️'; // Default to sunny
}

/**
 * Get alert level color
 */
export function getAlertLevelColor(alertLevel: string | null | undefined): string {
  if (!alertLevel) return '#52c41a'; // Green for no alert
  
  const lower = alertLevel.toLowerCase();
  
  if (lower.includes('red') || lower.includes('severe') || lower.includes('danger')) return '#cf1322';
  if (lower.includes('orange') || lower.includes('high') || lower.includes('warning')) return '#fa8c16';
  if (lower.includes('yellow') || lower.includes('moderate') || lower.includes('caution')) return '#fadb14';
  if (lower.includes('green') || lower.includes('low') || lower.includes('normal')) return '#52c41a';
  
  return '#1890ff'; // Default blue
}

/**
 * Format temperature with unit
 */
export function formatTemperature(temp: number | string | null | undefined, unit?: string): string {
  if (temp === null || temp === undefined) return 'N/A';
  const tempNum = typeof temp === 'string' ? parseFloat(temp) : temp;
  if (isNaN(tempNum)) return 'N/A';
  return `${Math.round(tempNum)}°${unit || 'C'}`;
}

/**
 * Conversion factor from m/s to km/h
 */
export const MS_TO_KMH = 3.6;

/**
 * Convert wind speed from m/s to km/h for display
 * Wind data is stored in m/s (matching Tomorrow.io API and station observations)
 * but displayed in km/h for user convenience
 */
export function convertWindToKmh(valueMs: number | string | null | undefined): number | null {
  if (valueMs === null || valueMs === undefined) return null;
  const numValue = typeof valueMs === 'string' ? parseFloat(valueMs) : valueMs;
  if (isNaN(numValue)) return null;
  return numValue * MS_TO_KMH;
}

/**
 * Format wind speed with direction
 * Note: Assumes speed is already in display units (km/h)
 */
export function formatWind(speed: number | string | null | undefined, direction?: string | null, unit?: string): string {
  if (speed === null || speed === undefined) return 'N/A';
  const speedNum = typeof speed === 'string' ? parseFloat(speed) : speed;
  if (isNaN(speedNum)) return 'N/A';
  
  const windSpeed = `${Math.round(speedNum)} ${unit || 'km/h'}`;
  if (direction) {
    return `${windSpeed} ${direction}`;
  }
  return windSpeed;
}

/**
 * Format precipitation with unit
 */
export function formatPrecipitation(precip: number | string | null | undefined, unit?: string): string {
  if (precip === null || precip === undefined) return '0';
  const precipNum = typeof precip === 'string' ? parseFloat(precip) : precip;
  if (isNaN(precipNum)) return '0';
  return `${precipNum.toFixed(1)} ${unit || 'mm'}`;
}

/**
 * Get parameter value by name from parameters array
 * Note: Units are managed via forecast_fields table, not in parameter data
 */
export function getParameterValue(
  parameters: Array<{ name: string; value: string | null; modified?: number | null }>,
  paramName: string
): { value: string | null; modified?: number | null } {
  const param = parameters.find(p => p.name.toLowerCase() === paramName.toLowerCase());
  return param ? { value: param.value, modified: param.modified } : { value: null, modified: null };
}

/**
 * Parse date/time string and return Date object
 */
export function parseDateTime(dateTime: string): Date {
  // Handle multiple formats
  if (dateTime.includes('T')) {
    return new Date(dateTime);
  }
  // Handle YYYYMMDDHHMMSS format
  if (/^\d{14}$/.test(dateTime)) {
    const year = dateTime.substring(0, 4);
    const month = dateTime.substring(4, 6);
    const day = dateTime.substring(6, 8);
    const hour = dateTime.substring(8, 10);
    const minute = dateTime.substring(10, 12);
    const second = dateTime.substring(12, 14);
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  }
  return new Date(dateTime);
}

/**
 * Format time for display
 */
export function formatTime(date: Date, timezone?: string): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return 'Invalid';
  
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  });
}

/**
 * Format date for display
 */
export function formatDate(date: Date, timezone?: string): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return 'Invalid';
  
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  });
}

